import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Printer, RefreshCw, Hash } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const LABEL_SIZES = [
  { id: 'small', label: 'Small (40x20mm)', w: 200, h: 100 },
  { id: 'medium', label: 'Medium (50x30mm)', w: 260, h: 140 },
  { id: 'large', label: 'Large (60x40mm)', w: 320, h: 180 },
];

const BarcodePrintModal = ({ product, onClose, onBarcodeUpdate }) => {
  const svgRef = useRef(null);
  const [barcode, setBarcode] = useState(product.barcode || '');
  const [labelSize, setLabelSize] = useState('medium');
  const [copies, setCopies] = useState(1);
  const [showPrice, setShowPrice] = useState(true);
  const [error, setError] = useState(null);

  const generateRandomBarcode = () => {
    // Generate a valid EAN-13 compatible 12-digit number (checksum auto-calculated by JsBarcode)
    const prefix = '200'; // Internal use prefix
    const random = Array.from({ length: 9 }, () => Math.floor(Math.random() * 10)).join('');
    const code = prefix + random;
    setBarcode(code);
  };

  useEffect(() => {
    if (!barcode || !svgRef.current) {
      setError(barcode ? null : 'Enter or generate a barcode');
      return;
    }
    setError(null);
    try {
      // Try EAN-13 first (if 12-13 digits), fallback to CODE128
      const isEan = /^\d{12,13}$/.test(barcode);
      JsBarcode(svgRef.current, barcode, {
        format: isEan ? 'EAN13' : 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 14,
        margin: 6,
        lineColor: '#000',
        background: '#fff',
        valid: (valid) => {
          if (!valid) setError('Invalid barcode format');
        },
      });
    } catch (e) {
      // Fallback to CODE128 if EAN fails
      try {
        JsBarcode(svgRef.current, barcode, {
          format: 'CODE128',
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 14,
          margin: 6,
          lineColor: '#000',
          background: '#fff',
        });
        setError(null);
      } catch {
        setError('Cannot generate barcode for this value');
      }
    }
  }, [barcode]);

  const handlePrint = () => {
    const size = LABEL_SIZES.find(s => s.id === labelSize);
    const svgEl = svgRef.current;
    if (!svgEl || error) return;

    const svgData = new XMLSerializer().serializeToString(svgEl);
    const printWindow = window.open('', '_blank', 'width=600,height=400');
    if (!printWindow) return;

    const labels = Array.from({ length: copies }, () => `
      <div class="label" style="width:${size.w}px;height:${size.h}px;border:1px dashed #ccc;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;padding:6px;margin:4px;page-break-inside:avoid;box-sizing:border-box;">
        <div style="font-size:10px;font-weight:700;text-align:center;margin-bottom:2px;max-width:${size.w - 16}px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">
          ${product.name}
        </div>
        <div>${svgData}</div>
        ${showPrice ? `<div style="font-size:13px;font-weight:700;margin-top:2px;">\u20AC${product.price?.toFixed(2) || '0.00'}</div>` : ''}
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Barcode - ${product.name}</title>
      <style>
        @page { margin: 5mm; }
        body { margin: 0; padding: 8px; font-family: Arial, sans-serif; }
        .labels { display: flex; flex-wrap: wrap; }
        @media print { .label { border: none !important; } }
      </style>
      </head><body>
        <div class="labels">${labels}</div>
        <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  const handleSaveBarcode = async () => {
    if (!barcode || barcode === product.barcode) return;
    if (onBarcodeUpdate) {
      await onBarcodeUpdate(product.id, barcode);
    }
  };

  const size = LABEL_SIZES.find(s => s.id === labelSize);

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          className="w-[480px] max-h-[85vh] bg-white overflow-y-auto"
          data-testid="barcode-print-modal"
          onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#1e3a5f]">Print Barcode</h3>
            <p className="text-sm text-slate-500">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="barcode-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Barcode input */}
          <div>
            <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Barcode</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={barcode}
                onChange={e => setBarcode(e.target.value)}
                placeholder="Enter barcode or generate one"
                className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm"
                data-testid="barcode-input"
              />
              <button
                onClick={generateRandomBarcode}
                className="px-3 py-2 bg-slate-100 border-2 border-slate-200 hover:bg-slate-200 transition-colors flex items-center gap-1.5 text-sm text-slate-700"
                title="Generate barcode"
                data-testid="generate-barcode-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Generate
              </button>
            </div>
            {barcode && barcode !== product.barcode && (
              <button
                onClick={handleSaveBarcode}
                className="mt-1.5 text-xs text-[#22c55e] font-medium hover:underline"
                data-testid="save-barcode-btn"
              >
                Save this barcode to product
              </button>
            )}
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Preview</label>
            <div className="flex justify-center p-4 bg-slate-50 border-2 border-slate-100">
              <div
                style={{ width: size.w, height: size.h }}
                className="bg-white border border-dashed border-slate-300 flex flex-col items-center justify-center p-1.5"
                data-testid="barcode-preview"
              >
                <p className="text-[10px] font-bold text-center truncate max-w-full mb-0.5">{product.name}</p>
                {barcode ? (
                  <svg ref={svgRef} />
                ) : (
                  <div className="flex items-center gap-1 text-slate-400 text-xs">
                    <Hash className="w-3 h-3" /> No barcode
                  </div>
                )}
                {showPrice && barcode && (
                  <p className="text-xs font-bold mt-0.5">{'\u20AC'}{product.price?.toFixed(2)}</p>
                )}
                {error && <p className="text-[9px] text-red-500 mt-1">{error}</p>}
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Label Size</label>
              <select
                value={labelSize}
                onChange={e => setLabelSize(e.target.value)}
                className="w-full px-2 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="label-size-select"
              >
                {LABEL_SIZES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Copies</label>
              <input
                type="number"
                min="1"
                max="100"
                value={copies}
                onChange={e => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2 py-2 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                data-testid="copies-input"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={showPrice}
                  onChange={e => setShowPrice(e.target.checked)}
                  className="w-4 h-4 accent-[#22c55e]"
                  data-testid="show-price-checkbox"
                />
                <span className="text-sm text-slate-700">Show price</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="vf-btn-secondary flex-1">Cancel</button>
          <button
            onClick={handlePrint}
            disabled={!barcode || !!error}
            className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="print-barcode-btn"
          >
            <Printer className="w-4 h-4" />
            Print {copies > 1 ? `${copies} Labels` : 'Label'}
          </button>
        </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default BarcodePrintModal;
